<template>
    <div
        ref="treeSelect"
        class="tree-select"
        :class="{ 'input-disabled': disabled, 'el-input': true, 'el-input--small': size === 'small' }"
        @mouseenter.capture.stop="hovering = true"
        @mouseout.capture.stop="hovering = false"
    >
        <div
            class="tree-select__inputArea el-input__inner"
            ref="inputArea"
            @click="focusInput"
            :style="inputAreaStyle"
        >
            <transition name="fade">
                <span class="icon-wrapper" v-if="showClear">
                    <i class="el-icon-error" @click="clear"></i>
                </span>
            </transition>
            <template v-if="multiple">
                <el-tag
                    v-for="(item, index) in selectedNodes4Show"
                    :key="item[nodeKey]"
                    type="info"
                    :closable="!(disabled || readonly)"
                    size="medium"
                    @close="deleteTag($event, item)"
                    disable-transitions
                >
                    <span class="el-select__tags-text">
                        {{
                        tagRender ? tagRender(item) : typeof props.label === 'string' ? item[props.label] : props.label(item)
                        }}
                    </span>
                </el-tag>
                <el-tag
                    v-if="showEllipsis && selectedNodes4Show.length < selectedNodes.length"
                    type="info"
                    :style="{ cursor: 'pointer' }"
                    :key="showEllipsis.toString()"
                >{{ ellipsisText }}</el-tag>
            </template>
            <template v-else>
                <span class="single-select-tag">
                    {{
                    selectedNodes.length
                    ? tagRender
                    ? tagRender(selectedNodes[0])
                    : typeof props.label === 'string'
                    ? selectedNodes[0][props.label]
                    : props.label(selectedNodes[0])
                    : ''
                    }}
                </span>
            </template>
            <template v-show="!(disabled || readonly)">
                <el-popover
                    ref="treePopover"
                    placement="bottom-start"
                    trigger="manual"
                    :popper-class="popperClass"
                    v-model="treeVisible"
                >
                    <div class="tree-select__treeWrap">
                        <el-tree
                            v-bind="$attrs"
                            ref="tree"
                            :node-key="nodeKey"
                            :data="treeData"
                            :props="innerProps"
                            :show-checkbox="multiple"
                            :check-strictly="!multiple"
                            :highlight-current="!multiple"
                            :check-on-click-node="checkOnClickNode"
                            @node-click="handleNodeClick"
                            @check-change="handleCheckChange"
                            @check="handleCheck"
                            :filter-node-method="filterNode"
                        ></el-tree>
                    </div>
                </el-popover>
                <span
                    class="place-holder"
                    v-if="!(selectedNodes.length || this.focused || this.keyword)"
                    :style="placeHolderStyle"
                >{{ placeholder }}</span>
                <el-input
                    :size="size"
                    ref="input"
                    v-show="!(disabled || readonly)"
                    :style="inputStyle"
                    v-model="keyword"
                    v-popover:treePopover
                    class="tree-select__innerInput search_poppver"
                    @focus="handleFocus"
                    @blur="handleBlur"
                ></el-input>
            </template>
        </div>
    </div>
</template>

<script>
import tsMixins from './tree-select-mixins';
import formatDataToTree from './formatDataToTree';
import { addResizeListener, removeResizeListener } from '../../utils/resize-event';

export default {
    name: 'XxTreeSelect',
    mixins: [tsMixins],
    props: {
        name: {
            type: String,
            default: 'TreeSelect'
        },
        value: [Number, String, Array],
        data: Array,
        props: Object,
        multiple: {
            type: Boolean,
            default: true
        },
        disabledKeys: Array, // todo
        selectLevel: [Number, String, Array],
        selectOnHalfCheck: Boolean,
        hideTreeOnTagDel: Boolean,
        tagRender: Function,
        placeholder: {
            type: String,
            default: ''
        },
        leafOnly: {
            type: Boolean,
            default: false
        },
        checkOnClickNode: {
            type: Boolean,
            default: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        // 每个节点的唯一标识
        nodeKey: {
            type: String,
            default: 'id'
        },
        // v-model绑定的对象，接收input事件时，要抛出node的哪个属性
        valueKey: {
            type: String,
            default: 'id'
        },
        size: {
            type: String,
            default: 'medium'
        },
        clearTextWhenNotMatch: {
            type: Boolean,
            default: false
        },
        popperClass: {
            type: String,
            default: ''
        },
        // 多选情况下，筛选在输入框中的tag的list的函数，输入是当前所有选中的nodes列表，返回是期望渲染的nodes列表
        // 示例： 只显示前两个：
        //  :tagFilter="(node,index)=>index<2"
        tagFilter: {
            type: Function,
            default: undefined
        },
        // 多选情况下，当输入框中展示的tag数量少于实际选择的tage数量时，显示一个省略号标签
        // 示例1：显示省略号
        // :showEllipsis="true"
        // 示例2：显示自定义文案
        // showEllipsis="展开剩余"
        // 示例3：显示动态文案
        // :showEllipsis="(nodes)=>{return `展开剩余${nodes.length-2}条`}"
        showEllipsis: {
            type: [Boolean, String, Function],
            default: function () {
                return function (selectedNodes) {
                    if (selectedNodes && this.selectedNodes4Show) {
                        return `+${selectedNodes.length - this.selectedNodes4Show.length}`;
                    }
                    return '...';
                };
            }
        }
    },
    data() {
        return {
            keyword: '',
            selectedNodes: [],
            treeVisible: false,
            hovering: false,
            focused: false
        };
    },
    computed: {
        ellipsisText() {
            if (typeof this.showEllipsis === 'boolean') {
                return '...';
            }
            if (typeof this.showEllipsis === 'string') {
                return this.showEllipsis;
            }
            if (typeof this.showEllipsis === 'function') {
                return this.showEllipsis(this.selectedNodes);
            }
            return '...';
        },
        selectedNodes4Show() {
            if (this.tagFilter) {
                return this.selectedNodes.filter(this.tagFilter);
            }
            return this.selectedNodes;
        },
        innerProps() {
            // todo 处理自定义label,id,children
            if (this.disabledKeys) {
                return {
                    ...this.props,
                    disabled: this.isNodeDisabled
                };
            }
            return this.props;
        },
        treeData() {
            return formatDataToTree(this.data);
        },
        checkedNodes() {
            return this.$refs.tree.getCheckedNodes();
        },
        minHeight() {
            let h = 30;
            switch (this.size) {
                case 'medium':
                    h = 32;
                    break;
                case 'small':
                    h = 32;
                    break;
                default:
                    break;
            }
            return h;
        },
        unifyLineHeight() {
            return this.minHeight - 2;
        },
        inputAreaStyle() {
            return { 'min-height': `${this.minHeight}px`, 'padding-left': '5px', 'padding-right': '5px' };
            // return { height: this.minHeight + 'px' };
        },
        placeHolderStyle() {
            return { 'line-height': `${this.unifyLineHeight}px` };
        },
        inputStyle() {
            return {
                width: this.keywordsWidth,
                'line-height': `${this.unifyLineHeight}px`
            };
        }
    },
    watch: {
        value: {
            handler(val) {
                this.updateSelectFromInput(); // 就是setCheckedKeys
            },
            deep: true
        },
        treeData: {
            handler() {
                this.updateSelectFromInput();
            }
        },
        keyword(val) {
            if (val) {
                this.$refs.tree.filter(val);
            }
        }
    },
    // 执行ref.setChecked后，ref.getChecked值会立即变更
    methods: {
        resetInputHeight() {
            this.$nextTick(() => {
                const inputArea = this.$refs.inputArea;
                const treeSelect = this.$refs.treeSelect;
                // let ibr = this.$refs.inputBorderRender;
                const sizeInMap = 32;
                const inputAreaWrapperHeight = inputArea.clientHeight + (inputArea.clientHeight > sizeInMap ? 6 : 0);
                const treeSelectHeight = this.selectedNodes4Show.length === 0 ? sizeInMap : Math.max(inputArea ? inputAreaWrapperHeight : 0, sizeInMap);
                treeSelect.style.height = `${treeSelectHeight}px`;
            });
        },
        clear() {
            this.selectedNodes = [];
            this.emitMsg([]);
        },
        isNodeDisabled(data, node) {
            return (
                !!this.disabledKeys &&
                !!this.disabledKeys.length &&
                (this.disabledKeys.includes(data.id) ||
                    this.disabledKeys.includes(+data.id) ||
                    (data.children &&
                        data.children.length &&
                        data.children.every(childData => this.disabledKeys.includes(childData.id) || this.disabledKeys.includes(+childData.id))))
            );
        },

        deleteTag(e, data) {
            // console.log(
            //     'before checked keys = ',
            //     this.$refs.tree.getCheckedNodes()
            // );
            // let key = data[this.nodeKey];
            // console.log('key = ', key);
            this.$refs.tree.setChecked(data, false, true);
            // let cKeys = this.$refs.tree.getCheckedKeys();
            let treeCheckedNodes = this.$refs.tree.getCheckedNodes();

            let selectedNodes = treeCheckedNodes.filter(this.filterNodesBySettings); // 这里拿到的就是取消过后的数据了。非选择顺序
            // console.log('treeCheckedKeys = ', cKeys);
            // console.log('treeCheckedNodes = ', treeCheckedNodes);
            // console.log('selectedNodes = ', selectedNodes);
            this.emitMsg(selectedNodes);
            // this.$emit('input', this.formatValue(selectedNodes));
            this.hideTreeOnTagDel && this.setTreeInvisible();
        },

        /**
         * 把v-model的数据映射到select上（非常重要的方法！
         */
        updateSelectFromInput() {
            let focus = this.treeVisible;
            let val = this.value;
            if (val === '' || val === -1) {
                val = [];
            }
            if (!Array.isArray(val)) val = [val];
            this.setSelectNodes(this.getSelectedNodesFromData(val));
            this.keyword = '';
            !focus && this.$refs.input && this.$refs.input.$refs.input.blur();
            if (!this.disabled && !this.readonly) {
                this.$refs.tree && this.$refs.tree.setCheckedKeys(this.selectedNodes.map(i => i[this.nodeKey]));
            }
        },

        handleCheckChange(data, checked, indeterminate) {
            this.$emit('check-change', data, checked, indeterminate);
        },
        formatValue(selectedNodes) {
            // selectedNodes.length>1 此时肯定是多选模式，抛出数组就完了
            if (selectedNodes.length > 1) {
                return selectedNodes.map(node => node[this.valueKey]);
            }
            // selectedNodes.length === 0||1 此时需要根据绑定值的类型做判断
            if (Array.isArray(this.value)) {
                // 绑定是数组，也抛出数组。
                return selectedNodes.length ? selectedNodes.map(node => node[this.valueKey]) : [];
            }
            if (typeof this.value === 'number') {
                // 绑定是数字，也抛出数字，没选就抛-1
                return selectedNodes.length ? +selectedNodes[0][this.valueKey] : -1;
            }
            if (typeof this.value === 'string') {
                // 绑定是字符串，也抛出字符串，没选就抛''
                return selectedNodes.length ? `${selectedNodes[0][this.valueKey]}` : '';
            }
        },

        nodeNoChange(newNodes, oldNodes) {
            let newNodeKeys = newNodes.map(i => i[this.nodeKey]);
            let oldNodeKeys = oldNodes.map(i => i[this.nodeKey]);
            return JSON.stringify(newNodeKeys) === JSON.stringify(oldNodeKeys);

        },
        /**
         * 用户通过点击树节点选择和取消选择时的事件
         * 用来更新input数据
         * @param node
         * @param checkedStatus
         */
        handleCheck(node, checkedStatus) {
            // console.log('check', node);
            let { checkedNodes, checkedKeys, halfCheckedNodes, halfCheckedKeys } = checkedStatus;

            let selectedNodes = [];

            if (this.multiple) {
                selectedNodes = checkedNodes.filter(this.filterNodesBySettings);
            } else {
                let selectedNodeTmp = [node].filter(this.filterNodesBySettings);
                selectedNodes = selectedNodeTmp.length ? selectedNodeTmp : this.selectedNodes;
            }
            // console.log('selectedNodes = ', selectedNodes);
            // console.log('this.checkedNodes = ', this.checkedNodes);
            let nodesChangeFromNone2One = selectedNodes.length !== 0 && this.selectedNodes.length === 0;
            let currentNodesChange = selectedNodes.length !== 0 && this.selectedNodes.length !== 0 && selectedNodes[0].id !== this.selectedNodes[0].id;
            // console.log('nodesChangeFromNone2One = ', nodesChangeFromNone2One);
            // console.log('currentNodesChange = ', currentNodesChange);
            if (!this.multiple && (nodesChangeFromNone2One || currentNodesChange)) {
                // 只有当单选模式时，会自动关闭树
                // 以下几种情况会关闭树
                // 1、之前没有选（this.selectedNodes.length===0），刚选上（selectedNodes.length!==0）
                // 2、之前选了，现在选的和之前不一样

                this.setTreeInvisible();
            }
            let nodeNoChange = this.nodeNoChange(selectedNodes, this.selectedNodes);
            this.setSelectNodes(selectedNodes);
            // console.log('this.selectedNodes = ', this.selectedNodes);
            // console.log('selectedNodes = ', selectedNodes);

            // this.selectedNodes = selectedNodes;
            if (!nodeNoChange) {
                this.emitMsg();
            }
        },
        emitMsg(selectedNodes) {
            let ids = this.formatValue(selectedNodes || this.selectedNodes);
            this.$emit('input', ids); // 就这句超重要
            this.$emit('selected-nodes-change', selectedNodes || this.selectedNodes);
        },
        handleNodeClick(data, b, c) {
            // let nodes = [...new Set([...this.checkedNodes, data])];
            // this.$refs.tree.setCheckedNodes(nodes);
        },
        filterNode(value, data) {
            if (!value) return true;
            // let node = this.$refs.tree.getNode(data);
            // console.log('node = ', node);
            // console.log('value = ', value);
            // console.log('data = ', data);
            // console.log('this.props.label = ',this.props.label);

            // console.log('filterNode');
            // 包含找父节点
            // for (let i = node.level; i > 0; i--) {
            //     let label = typeof this.props.label === 'string' ? node.data[this.props.label] : this.props.label(node.data);
            //     if (label.indexOf(value) > -1) {
            //         // console.log('label=',label)
            //         // console.log('value=',value)
            //         return true;
            //     }
            //     node = node.parent;
            // }
            let label = typeof this.props.label === 'string' ? data[this.props.label] : this.props.label(data);
            let r = label.indexOf(value) > -1;

            return r;
        },
        otherDomClick(e) {
            this.$refs.tree && this.$refs.tree.filter();
            if (!this.$refs.treeSelect.contains(e.target)) {
                this.setTreeInvisible();
            }
        },
        handleResize() {
            this.resetInputHeight();
        }
    },

    mounted() {
        addResizeListener(this.$refs.inputArea, this.handleResize);
        window.addEventListener('click', this.otherDomClick);
        this.updateSelectFromInput();
    },
    beforeDestroy() {
        removeResizeListener(this.$refs.inputArea, this.handleResize);
        window.removeEventListener('click', this.otherDomClick);
    }
};
</script>

<style lang="scss" scoped>
.tree-select__innerInput {
    /deep/ .el-input__inner {
        height: 30px !important;
    }
}
.input-disabled {
    background: #f9fafc;
}
.icon-wrapper {
    position: absolute;
    right: 10px;
    z-index: 1000;
    // display: inline-block;
    height: 32px;
    width: 18px;
    cursor: pointer;
}
.el-icon-error {
    position: absolute;
    right: 0;
    // display: inline-block;
    line-height: 30px;
    color: #bbb;
    &:hover {
        color: #909399;
    }
}
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
}

.tree-select__inputArea {
    height: auto !important;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    position: relative;
    top: 50%;
    transform: translateY(-50%);
    box-sizing: border-box;
    border: 1px solid #dcdfe6;
    min-height: 30px;
    border-radius: 4px;
    line-height: 30px !important;
}
.tree-select__innerInput {
    margin-left: 10px;
    /deep/ input {
        background-color: rgba(1, 1, 1, 0);
        // opacity: 0;
        // width: 1px !important;
        // height: 30px;
        padding: 0 !important;
        // display:block
        // line-height:30px;
    }
}
.place-holder {
    margin-left: 12px;
    color: #999;
    font-size: 14px;
}

.tree-select {
    position: relative;
    display: block;

    .el-tag {
        margin-left: 6px;
    }
    &__innerInput {
        border: none;
        width: 100px;
        /deep/ input {
            border: none;
        }
    }

    &__treeWrap {
        background: #fff;
        width: 220px;
        height: 320px;
        overflow: auto;
    }
}
.single-select-tag {
    margin-left: 10px;
    line-height: 30px;
}
</style>
